import type { UIMessage } from "ai";
import { z } from "zod";

const generatedAssetCopyEditOptionSchema = z.object({
  caption: z.string().trim().min(1).max(2_200),
  hashtags: z.array(z.string().trim().min(1).max(80)).max(30).nullable(),
});

const generatedAssetCopyEditPreviewSchema = z.object({
  render: z.object({ component: z.literal("generated_asset_copy_edit_confirmation") }),
  summary: z.string().trim().min(1).max(600).nullable().optional(),
  instruction: z.string().trim().min(3).max(2_000),
  generatedAssetId: z.uuid(),
  scheduledPostId: z.uuid(),
  blockId: z.string().trim().min(1).max(120),
  batchId: z.uuid(),
  batchSourceType: z.enum(["standalone", "campaign"]).nullable().optional(),
  draftUpdatedAt: z.string().trim().min(1).nullable().optional(),
  slideIndex: z.number().int().positive().nullable().optional(),
  slideId: z.string().trim().min(1).max(120).nullable().optional(),
  batchName: z.string().trim().min(1).max(200).nullable().optional(),
  currentCaption: z.string().trim().min(1).max(2_200).nullable().optional(),
  currentHashtags: z.array(z.string().trim().min(1).max(80)).max(30).nullable().optional(),
  options: z.array(generatedAssetCopyEditOptionSchema).min(1).max(3),
});

export type AssistantGeneratedAssetCopyEditOption = z.infer<
  typeof generatedAssetCopyEditOptionSchema
>;

export type AssistantGeneratedAssetCopyEditPreviewReference = {
  id: string;
  summary: string | null;
  instruction: string;
  generatedAssetId: string;
  scheduledPostId: string;
  blockId: string;
  batchId: string;
  batchSourceType: "standalone" | "campaign" | null;
  draftUpdatedAt: string | null;
  slideIndex: number | null;
  slideId: string | null;
  batchName: string | null;
  currentCaption: string | null;
  currentHashtags: string[] | null;
  options: AssistantGeneratedAssetCopyEditOption[];
};

export function getAssistantGeneratedAssetCopyEditPreviewReferences(
  message: Pick<UIMessage, "parts">,
): AssistantGeneratedAssetCopyEditPreviewReference[] {
  const references: AssistantGeneratedAssetCopyEditPreviewReference[] = [];

  for (const [index, part] of message.parts.entries()) {
    const candidate = part as {
      type?: string;
      state?: string;
      toolCallId?: string;
      output?: unknown;
    };

    if (
      candidate.type !== "tool-preview_generated_asset_copy_edit" ||
      candidate.state !== "output-available"
    ) {
      continue;
    }

    const reference = parseAssistantGeneratedAssetCopyEditPreviewReference(
      candidate.output,
      typeof candidate.toolCallId === "string" ? candidate.toolCallId : `asset-copy-edit-${index}`,
    );
    if (reference) {
      references.push(reference);
    }
  }

  return references;
}

export function isAssistantGeneratedAssetCopyEditProposalStale(
  messages: readonly Pick<UIMessage, "id" | "role">[],
  proposalMessageId: string,
) {
  const proposalMessageIndex = messages.findIndex((message) => message.id === proposalMessageId);
  if (proposalMessageIndex === -1) {
    return false;
  }

  return messages.slice(proposalMessageIndex + 1).some((message) => message.role === "user");
}

function parseAssistantGeneratedAssetCopyEditPreviewReference(
  value: unknown,
  id: string,
): AssistantGeneratedAssetCopyEditPreviewReference | null {
  const parsed = generatedAssetCopyEditPreviewSchema.safeParse(value);
  if (!parsed.success) {
    return null;
  }

  return {
    id,
    summary: parsed.data.summary?.trim() || null,
    instruction: parsed.data.instruction,
    generatedAssetId: parsed.data.generatedAssetId,
    scheduledPostId: parsed.data.scheduledPostId,
    blockId: parsed.data.blockId,
    batchId: parsed.data.batchId,
    batchSourceType: parsed.data.batchSourceType ?? null,
    draftUpdatedAt: parsed.data.draftUpdatedAt ?? null,
    slideIndex: parsed.data.slideIndex ?? null,
    slideId: parsed.data.slideId ?? null,
    batchName: parsed.data.batchName?.trim() || null,
    currentCaption: parsed.data.currentCaption?.trim() || null,
    currentHashtags: parsed.data.currentHashtags ?? null,
    options: parsed.data.options,
  };
}
